import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Grid, Typography, Box, Button, List, ListItem, ListItemIcon, ListItemText, Divider } from "@material-ui/core";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import useStyles from "./ServiceAreas.Styles";
import InspectionImg from "../../Assets/Images/inspection.webp";
import ProductivityImg from "../../Assets/Images/productivity.webp";
import CallToAction from "../../Components/CallToAction/CallToAction";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import CallIcon from '@mui/icons-material/Call';
import RequestQuoteIcon from '@mui/icons-material/RequestQuoteTwoTone';
import CardContent from "@material-ui/core/CardContent";
import SamplingImage from "./../../Assets/Images/sampling.webp"
import { Helmet } from "react-helmet";

function ServiceAreas() {
    const location = useLocation();
    const classes = useStyles();

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace("#", "");
            const element = document.getElementById(id);
            if (element) {
                setTimeout(() => {
                    const yOffset = -175; // adjust based on navbar height
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }, 0);
            }
        }
    }, [location]);

    const city = location.pathname.split("/")[1];
    const isGeneralAreaPage = city === "service-areas" || city === "";

    const formattedCity = !isGeneralAreaPage
        ? (() => {
            const formatted = city
                .split('-')
                .map(word => (word.toLowerCase() === 'd' ? "d'" : word.charAt(0).toUpperCase() + word.slice(1)))
                .join(' ')
                .replace("d' ", "d'");

            const washingtonSlugs = [
                "spokane",
                "newport",
                "deer-park",
                "colbert",
                "mead",
                "spokane-county",
                "pend-oreille-county",
                "stevens-county"
            ];

            const isWashington = washingtonSlugs.includes(city);

            return isWashington
                ? `${formatted}, Washington`
                : `${formatted}, Idaho`;
        })()
        : "";

    // 2. CANONICAL URL
    const canonicalUrl = isGeneralAreaPage
        ? "https://chinookwater.com/service-areas"
        : `https://chinookwater.com/${city}`;

    // 3. BREADCRUMB ITEMS (Home URL without trailing slash for parity with your other site)
    const breadcrumbItems = [
        { name: "Home", item: "https://chinookwater.com" }
    ];

    if (!isGeneralAreaPage) {
        breadcrumbItems.push({
            name: formattedCity,
            item: canonicalUrl
        });
    } else if (location.pathname === "/service-areas") {
        breadcrumbItems.push({
            name: "Service Areas",
            item: canonicalUrl
        });
    }

    const regionLabel = "North Idaho and Eastern Washington";

    const mainTitle = isGeneralAreaPage
        ? <>Chinook Water Testing offers Certified Well Water Testing, Flow Rate Testing (GPM) & System Inspections. <span style={{ textDecoration: 'underline', fontWeight: 500 }}>Our Service Areas include all of {regionLabel}</span>.</>
        : <>Chinook Water Testing offers Certified Well Water Testing, Flow Rate Testing (GPM) & System Inspections in <span style={{ textDecoration: 'underline', fontWeight: 500 }}>{formattedCity}, and the Surrounding Areas</span>. Our Service Areas include all of {regionLabel}.</>;

    return (
        <section className={classes.root}>
            <Helmet>
                <title>
                    {isGeneralAreaPage
                        ? "Well Water Testing Near Me - North Idaho & Eastern WA | Flow Rate (GPM), FHA/VA Testing"
                        : `Well Water Testing Near Me - ${formattedCity} | Flow Rate (GPM), FHA/VA Testing`
                    }
                </title>
                <meta
                    name="description"
                    content={isGeneralAreaPage
                        ? "Certified well water testing, flow rate (GPM), and system inspections for real estate across North Idaho and Eastern Washington. FHA/VA/USDA-compliant sampling with fast results."
                        : `Certified well water testing, flow rate (GPM), and system inspections for real estate in ${formattedCity}. FHA/VA/USDA-compliant sampling with fast results.`
                    }
                />
                <meta
                    name="keywords"
                    content={
                        isGeneralAreaPage
                            ? "well water testing North Idaho, flow rate testing Eastern Washington, VA FHA USDA loan water test"
                            : `well water testing ${formattedCity}, GPM flow test ${formattedCity}, VA loan water testing ${formattedCity}`
                    }
                />
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph */}
                <meta
                    property="og:title"
                    content={isGeneralAreaPage
                        ? "Well Water Testing Near Me - North Idaho & Eastern WA | Flow Rate (GPM), FHA/VA Testing"
                        : `Well Water Testing Near Me - ${formattedCity} | Flow Rate (GPM), FHA/VA Testing`
                    }
                />
                <meta
                    property="og:description"
                    content={
                        isGeneralAreaPage
                            ? "Certified well water testing, flow rate (GPM), and system inspections for real estate across North Idaho and Eastern Washington. FHA/VA/USDA-compliant sampling with fast results."
                            : `Certified well water testing, flow rate (GPM), and system inspections for real estate in ${formattedCity}. FHA/VA/USDA-compliant sampling with fast results.`
                    }
                />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content="https://chinookwater.com/chinookLogo.webp" />
                <meta property="og:image:alt" content="Chinook Water Testing company logo" />
                <meta property="og:site_name" content="Chinook Water Testing" />

                {/* Twitter (parity with OG; no image changes) */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={isGeneralAreaPage
                        ? "Well Water Testing Near Me - North Idaho & Eastern WA | Flow Rate (GPM), FHA/VA Testing"
                        : `Well Water Testing Near Me - ${formattedCity} | Flow Rate (GPM), FHA/VA Testing`
                    }
                />
                <meta
                    name="twitter:description"
                    content={
                        isGeneralAreaPage
                            ? "Certified well water testing, flow rate (GPM), and system inspections for real estate across North Idaho and Eastern Washington. FHA/VA/USDA-compliant sampling with fast results."
                            : `Certified well water testing, flow rate (GPM), and system inspections for real estate in ${formattedCity}. FHA/VA/USDA-compliant sampling with fast results.`
                    }
                />
                <meta name="twitter:image" content="https://chinookwater.com/chinookLogo.webp" />

                {/* FULL @graph JSON-LD — mirrors your other site’s pattern */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "@id": `${canonicalUrl}#webpage`,
                                "url": canonicalUrl,
                                "name": isGeneralAreaPage
                                    ? "Well Water Testing Near Me - North Idaho & Eastern WA | Flow Rate (GPM), FHA/VA Testing"
                                    : `Well Water Testing Near Me - ${formattedCity} | Flow Rate (GPM), FHA/VA Testing`,
                                "description": isGeneralAreaPage
                                    ? "Certified well water testing, flow rate (GPM), and system inspections for real estate across North Idaho and Eastern Washington. FHA/VA/USDA-compliant sampling with fast results."
                                    : `Certified well water testing, flow rate (GPM), and system inspections for real estate in ${formattedCity}. FHA/VA/USDA-compliant sampling with fast results.`,
                                "isPartOf": { "@id": "https://chinookwater.com/#website" },
                                "about": { "@id": "https://chinookwater.com/#business" },
                                "breadcrumb": {
                                    "@type": "BreadcrumbList",
                                    "itemListElement": breadcrumbItems.map((crumb, idx) => ({
                                        "@type": "ListItem",
                                        "position": idx + 1,
                                        "name": crumb.name,
                                        "item": crumb.item
                                    }))
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "mainEntity": [
                                    {
                                        "@type": "Question",
                                        "name": `Do you offer well water testing in ${formattedCity || "North Idaho and Eastern Washington"}?`,
                                        "acceptedAnswer": {
                                            "@type": "Answer",
                                            "text": `Yes. We provide certified well water sampling, bacteria, potability, and heavy metals testing in ${formattedCity || "all of North Idaho and Eastern Washington"}.`
                                        }
                                    },
                                    {
                                        "@type": "Question",
                                        "name": `Do you perform flow rate (GPM) testing in ${formattedCity || "the region"}?`,
                                        "acceptedAnswer": {
                                            "@type": "Answer",
                                            "text": "Yes. We conduct HUD 4000.1-compliant flow rate testing, VA/FHA/USDA loan verification, and full well production analysis."
                                        }
                                    },
                                    {
                                        "@type": "Question",
                                        "name": `Do you inspect water systems in ${formattedCity || "the area"}?`,
                                        "acceptedAnswer": {
                                            "@type": "Answer",
                                            "text": "Yes. We perform full operational checks, compliance inspections, and detailed reports for real estate and loan requirements."
                                        }
                                    }
                                ]
                            },
                            {
                                "@type": "ItemList",
                                "name": `Well Water Services in ${formattedCity || "North Idaho & Eastern WA"}`,
                                "itemListElement": [
                                    {
                                        "@type": "ListItem",
                                        "position": 1,
                                        "item": {
                                            "@type": "Service",
                                            "@id": "https://chinookwater.com/#service-water-testing",
                                            "name": "Water Sampling & Testing",
                                            "description": "Bacterial, potability, heavy metals, and VA/FHA/USDA/HUD loan compliance testing using a state-certified lab.",
                                            "provider": { "@id": "https://chinookwater.com/#business" },
                                            "url": `${canonicalUrl}#sampling-section`
                                        }
                                    },
                                    {
                                        "@type": "ListItem",
                                        "position": 2,
                                        "item": {
                                            "@type": "Service",
                                            "@id": "https://chinookwater.com/#service-flow-rate",
                                            "name": "Flow Rate Monitoring (GPM)",
                                            "description": "HUD 4000.1 productivity verification, loan-compliant flow testing, and well production analysis for real estate.",
                                            "provider": { "@id": "https://chinookwater.com/#business" },
                                            "url": `${canonicalUrl}#productivity-section`
                                        }
                                    },
                                    {
                                        "@type": "ListItem",
                                        "position": 3,
                                        "item": {
                                            "@type": "Service",
                                            "@id": "https://chinookwater.com/#service-system-inspection",
                                            "name": "System Inspection",
                                            "description": "Operational checks, compliance validation, and full inspection reports for private well and wastewater systems.",
                                            "provider": { "@id": "https://chinookwater.com/#business" },
                                            "url": `${canonicalUrl}#system-inspection-section`
                                        }
                                    }
                                ]
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <header className={classes.header}>
                <Box className={classes.titleBox}>
                    <Typography variant="h1" className={classes.title}>
                        {mainTitle}
                    </Typography>
                    <Typography className={classes.subText}>
                        Ready to schedule your service<span>{isGeneralAreaPage ? "" : ` in ${formattedCity}`}</span>?
                        <br />
                        Call us at{' '}
                        <a href="tel:+12083523909" className={`${classes.phoneLink}`}>
                            (208) 352-3909
                        </a>
                    </Typography>
                </Box>
            </header>

            <Grid container spacing={0} className={classes.sectionContainer}>
                {/* Section 1: Sampling & Testing */}
                <Grid item xs={12} id="sampling-section" className={classes.gridItem}>
                    <Grid
                        container
                        className={classes.mainGridContainer}
                        alignContent="center"
                        justifyContent="center"
                    >
                        <Grid item xs={12} className={classes.topGridItem}>
                            <Box
                                component={Link}
                                to="/"
                                style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#858a94' }}
                                aria-label="Back To Homepage"
                            >
                                <ArrowBackIosIcon style={{ cursor: 'pointer' }} />
                                <span className={classes.viewOptions}>BACK TO HOMEPAGE</span>
                            </Box>
                            {/* <ShareIcon
                                onClick={handleShare}
                                aria-label="Share this page"
                                style={{
                                    position: "absolute",
                                    right: 35,
                                    color: "#858a94",
                                    cursor: 'pointer',
                                }}
                            /> */}

                        </Grid>
                        {/* Image Container */}
                        <Grid
                            item
                            xs={12}
                            md={6}
                            className={classes.imageGridContainer}
                        >
                            <img
                                src={SamplingImage}
                                alt="Water Sampling and Testing Service Area"
                                className={classes.imageStyles}
                                loading="lazy"
                                data-ratio='600 / 450'
                            />
                        </Grid>
                        {/* Main Content */}
                        <Grid item xs={12} md={6} style={{ backgroundColor: "white" }}>
                            <Box className={classes.descriptionContainer}>
                                <Typography className={classes.sectionTitle}>
                                    Sampling & Testing
                                </Typography>
                                <Typography className={classes.subTitle}>
                                    Full Service Well Water Testing in <span>{isGeneralAreaPage ? "the Inland Northwest" : formattedCity}</span>
                                </Typography>

                                <Divider style={{ height: 2, marginTop: 14 }} />
                                <CardContent className={classes.description}>
                                    Chinook Water Testing offers professional well water sampling and lab-certified testing in{' '}
                                    <span>{isGeneralAreaPage ? "the Inland Northwest" : `${formattedCity}, and nearby areas`}</span>. Our services ensure your drinking water meets state health codes, real estate transaction standards, and VA, FHA, USDA & HUD loan requirements. Whether you are a homeowner, real estate agent, or loan officer, we deliver accurate, certified results for peace of mind.
                                </CardContent>
                                <List className={classes.bulletList}>
                                    {[
                                        "Bacterial and Potability Testing",
                                        "Heavy Metals Testing (upon request)",
                                        "VA, FHA, USDA & HUD Loan Compliance Testing",
                                        "State Certified Lab for all Testing"
                                    ].map((item, i) => (
                                        <ListItem key={i} className={classes.listItem}>
                                            <ListItemIcon><CheckCircleIcon className={classes.icon} /></ListItemIcon>
                                            <ListItemText primary={item} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Grid>
                        {/* Button view */}
                        <Grid item xs={12}>
                            <Grid container alignItems="center" className={classes.buttonView}>
                                <Grid item sm={2} md={6}></Grid>
                                <Grid item xs={12} sm={8} md={6}>
                                    <Box className={classes.buttonGroup}>
                                        <Button
                                            size="small"
                                            className={classes.contactButton}
                                            component={Link}
                                            to="/contact"
                                            startIcon={<RequestQuoteIcon />}
                                        >
                                            Get a Quote
                                        </Button>
                                        <Button
                                            size="small"
                                            className={classes.contactButton}
                                            component="a"
                                            startIcon={<CallIcon />}
                                            href="tel:+12083523909"
                                        >
                                            (208) 352-3909
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                </Grid>

                {/* Section 2: Flow Rate Monitoring */}
                <Grid item xs={12} id="productivity-section" className={classes.gridItem}>
                    <Grid
                        container
                        className={classes.mainGridContainer}
                        alignContent="center"
                        justifyContent="center"
                    >
                        <Grid item xs={12} className={classes.topGridItem}>
                            <Box
                                component={Link}
                                to="/"
                                style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#858a94' }}
                                aria-label="Back To Homepage"
                            >
                                <ArrowBackIosIcon style={{ cursor: 'pointer' }} />
                                <span className={classes.viewOptions}>BACK TO HOMEPAGE</span>
                            </Box>
                            {/* <ShareIcon
                                onClick={handleShare}
                                aria-label="Share this page"
                                style={{
                                    position: "absolute",
                                    right: 35,
                                    color: "#858a94",
                                    cursor: 'pointer',
                                }}
                            /> */}
                        </Grid>
                        {/* Image Container */}
                        <Grid
                            item
                            xs={12}
                            md={6}
                            className={classes.imageGridContainer}
                        >
                            <img
                                src={ProductivityImg}
                                alt="Flow Rate Monitoring Service Area"
                                className={classes.imageStyles}
                                loading="lazy"
                                data-ratio='600 / 450'
                            />
                        </Grid>
                        {/* Main Content */}
                        <Grid item xs={12} md={6} style={{ backgroundColor: "white" }}>
                            <Box className={classes.descriptionContainer}>
                                <Typography className={classes.sectionTitle}>
                                    Water Production
                                </Typography>
                                <Typography className={classes.subTitle}>
                                    Flow Rate Testing (GPM) in <span>{isGeneralAreaPage ? "the Inland Northwest" : formattedCity}</span>
                                </Typography>
                                <Divider style={{ height: 2, marginTop: 14 }} />
                                <CardContent className={classes.description}>
                                    Ensuring sufficient water flow (GPM) is critical for homeowners, real estate transactions, and loan approvals. Chinook Water Testing conducts flow rate monitoring to verify that private wells
                                    <span>{isGeneralAreaPage ? " " : ` in ${formattedCity}, `}</span>
                                    meet productivity standards, including VA, FHA, USDA & HUD home loan requirements. Our testing provides accurate flow rate measurements and detailed reporting to ensure compliance and system efficiency.
                                </CardContent>
                                <List className={classes.bulletList}>
                                    {[
                                        "HUD 4000.1 Productivity Verification",
                                        "VA, FHA, USDA, and HUD Loan Compliance Testing",
                                        "System Performance & Flow Rate Analysis",
                                        "Well Production Testing for Real Estate Sales"
                                    ].map((item, i) => (
                                        <ListItem key={i} className={classes.listItem}>
                                            <ListItemIcon><CheckCircleIcon className={classes.icon} /></ListItemIcon>
                                            <ListItemText primary={item} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Grid>
                        {/* Button view */}
                        <Grid item xs={12}>
                            <Grid container alignItems="center" className={classes.buttonView}>
                                <Grid item sm={2} md={6}></Grid>
                                <Grid item xs={12} sm={8} md={6}>
                                    <Box className={classes.buttonGroup}>
                                        <Button
                                            size="small"
                                            className={classes.contactButton}
                                            component={Link}
                                            to="/contact"
                                            startIcon={<RequestQuoteIcon />}
                                        >
                                            Get a Quote
                                        </Button>
                                        <Button
                                            size="small"
                                            className={classes.contactButton}
                                            component="a"
                                            href="tel:+12083523909"
                                            startIcon={<CallIcon />}
                                        >
                                            (208) 352-3909
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                </Grid>

                {/* Section 3: System Inspection */}
                <Grid item xs={12} id="system-inspection-section">
                    <Grid
                        container
                        className={classes.mainGridContainer}
                        alignContent="center"
                        justifyContent="center"
                    >
                        <Grid item xs={12} className={classes.topGridItem}>
                            <Box
                                component={Link}
                                to="/"
                                style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#858a94' }}
                                aria-label="Back To Homepage"
                            >
                                <ArrowBackIosIcon style={{ cursor: 'pointer' }} />
                                <span className={classes.viewOptions}>BACK TO HOMEPAGE</span>
                            </Box>
                            {/* <ShareIcon
                                onClick={handleShare}
                                aria-label="Share this page"
                                style={{
                                    position: "absolute",
                                    right: 35,
                                    color: "#858a94",
                                    cursor: 'pointer',
                                }}
                            /> */}
                        </Grid>
                        {/* Image Container */}
                        <Grid
                            item
                            xs={12}
                            md={6}
                            className={classes.imageGridContainer}
                        >
                            <img
                                src={InspectionImg}
                                alt="Well water system inspection service areas"
                                className={classes.imageStyles}
                                loading="lazy"
                                data-ratio='600 / 450'
                            />
                        </Grid>
                        {/* Main Content */}
                        <Grid item xs={12} md={6} style={{ backgroundColor: "white" }}>
                            <Box className={classes.descriptionContainer}>
                                <Typography className={classes.sectionTitle}>
                                    System Inspection
                                </Typography>
                                <Typography className={classes.subTitle}>
                                    Water System Inspection in <span>{isGeneralAreaPage ? "the Inland Northwest" : formattedCity}</span>
                                </Typography>
                                <Divider style={{ height: 2, marginTop: 14 }} />
                                <CardContent className={classes.description}>
                                    Chinook Water Testing provides thorough system inspections for private well water and wastewater systems
                                    <span>{isGeneralAreaPage ? ". " : ` in ${formattedCity}, and the surrounding areas. `}</span>
                                    Our certified inspections ensure your water system operates safely, efficiently, and in compliance with state
                                    and local regulations. Whether you're buying, selling, or maintaining a property,
                                    our inspections help identify potential issues before they become costly problems.
                                </CardContent>
                                <List className={classes.bulletList}>
                                    {[
                                        "Operational checks and performance validation",
                                        "Consultation and recommendations for compliance",
                                        "Full compliance with state and local requirements",
                                        "Homebuyer and real estate transaction inspection"
                                    ].map((item, i) => (
                                        <ListItem key={i} className={classes.listItem}>
                                            <ListItemIcon><CheckCircleIcon className={classes.icon} /></ListItemIcon>
                                            <ListItemText primary={item} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Grid>
                        {/* Button view */}
                        <Grid item xs={12}>
                            <Grid container alignItems="center" className={classes.buttonView}>
                                <Grid item sm={2} md={6}></Grid>
                                <Grid item xs={12} sm={8} md={6}>
                                    <Box className={classes.buttonGroup}>
                                        <Button
                                            size="small"
                                            className={classes.contactButton}
                                            component={Link}
                                            to="/contact"
                                            startIcon={<RequestQuoteIcon />}
                                        >
                                            Get a Quote
                                        </Button>
                                        <Button
                                            size="small"
                                            className={classes.contactButton}
                                            component="a"
                                            href="tel:+12083523909"
                                            startIcon={<CallIcon />}
                                        >
                                            (208) 352-3909
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Call to Action */}
            <CallToAction />
        </section>
    );
}

export default ServiceAreas;
